<template>
	<div>
		<Disclosure :title="$t('text.publicInfoTitle')" :error="sectionMessage.error" :warning="sectionMessage.warning" :message="sectionMessage.message"
			:lock="!userMayEditFields || contentManagedByMyCity" :expanded="true" data-cy="publicInfo" ref="SECTION_publicInfo"
		>
			<!-- Would this be part of field.validations? -->
			<!-- :error-messages="productTitleErrors" -->
			<Field v-if="hasField('publicInfo', 'title', product)" typeName="Service" fieldName="title" v-model="product.fields.title"
				:disabled="productIsExternal"
				:required="currentLocaleRequiresTranslation"
				watchRequired
				@change="() => onChange('title')"
			/>

			<Field v-if="hasField('publicInfo','longDescription', product)" typeName="Service" fieldName="longDescription" v-model="product.fields.longDescription"
				:disabled="!userMayEditFields || contentManagedByMyCity"
				:required="currentLocaleRequiresTranslation"
				watchRequired
				:errorMessages="longDescriptionErrors"
				@change="() => onChange()"
			/>

			<!-- Need to add/remove required label on condition! Can this be handled by API? -->
			<!-- :required="!productIsExternal" -->
			<Field v-if="hasField('publicInfo','shortDescription', product)" typeName="Service" fieldName="shortDescription" v-model="product.fields.shortDescription"
				:disabled="!userMayEditFields"
				:required="!productIsExternal && currentLocaleRequiresTranslation"
				watchRequired
				:errorMessages="shortDescriptionErrors"
				@change="() => onChange()"
			/>

			<Field v-if="hasField('publicInfo','importantInfo', product)" typeName="Service" fieldName="importantInformation" v-model="product.fields.importantInformation"
				:disabled="!userMayEditFields || contentManagedByMyCity"
				@change="() => onChange()"
			/>
		</Disclosure>
	</div>
</template>

<script lang='ts'>
import ImportantInfo from './ImportantInfo.vue'
import TextArea from '@/components/common/TextArea.vue'
import Common from '@/mixins/Common.vue'
import Disclosure from '@/components/common/Disclosure.vue'
import Field from '@/components/fields/Field.vue'

type PublicInfo = {
	title: string,
	longDescription: string,
	shortDescription: string,
	importantInformation: string,
}

export default {
	components: { ImportantInfo, TextArea, Disclosure, Field },
	mixins: [ Common ],
	props: {
		product: {
			type: Object as () => { fields: PublicInfo },
			required: true
		},
		productIsExternal: Boolean,
		/** When true, section is locked because content is overwritten by MyCity importer. */
		contentManagedByMyCity: { type: Boolean, default: false },
	},
	data: () => ({
		sectionMessage: { error: false, warning: false, message: '' },
		productTitleErrors: [] as string[],
		longDescriptionErrors: [] as string[],
		shortDescriptionErrors: [] as string[],
		initData: {} as PublicInfo,
	}),
	computed: {
		currentLocaleRequiresTranslation() {
			const locale = this.$store.state.serviceLocale
			if (!locale) return false
			if (this.getRequiredTranslationLocaleCodes().includes(locale)) return true

			return [ 'title', 'longDescription', 'shortDescription' ]
				.some(fieldName => this.translationValueIsSet(this.product?.fields?.[fieldName]?.[locale]))
		},
	},
	watch: {
		longDescriptionErrors(n) {
			if (n.length) {
				this.setSectionError(this.sectionMessage, this.$t('text.longDescriptionRequired'))
			}
		},
		shortDescriptionErrors(n) {
			if (!this.productIsExternal && n.length) {
				this.setSectionError(this.sectionMessage, this.$t('text.shortDescriptionRequired'))
			}
		},
		productTitleErrors(n) {
			if (n.length) {
				this.setSectionError(this.sectionMessage, this.$t('text.titleRequiredError'))
			}
		},
	},
	methods: {
		hasChanged(): boolean {
			const data = {
				title: this.product?.fields.title,
				longDescription: this.product?.fields.longDescription,
				shortDescription: this.product?.fields.shortDescription,
				importantInformation: this.product?.fields.importantInformation,
			} as PublicInfo
			return JSON.stringify(data) !== JSON.stringify(this.initData)
		},
		validateAllFields() {
			let allFieldsAreValid = true
			this.resetSectionError(this.sectionMessage)

			const isTitleValid = this.validateTitle(true)
			const isLongDescriptionValid = this.validateLongDescription()
			const isShortDescriptionValid = this.validateShortDescription()

			if (!isTitleValid || !isLongDescriptionValid || !isShortDescriptionValid) {
				allFieldsAreValid = false
				this.setSectionError(this.sectionMessage, this.$t('text.missingFieldsError'))
			}

			return allFieldsAreValid
		},
		validateTitle(runValidation = false) {
			let isValid = true
			const hasAnyLocaleValue = this.validateTextLocales(this.product.fields.title)
			this.productTitleErrors = []

			if (runValidation && !hasAnyLocaleValue) {
				isValid = false
				this.productTitleErrors.push(this.$t('text.nameRequired'))
			}
			else if (!hasAnyLocaleValue) {
				isValid = false
				this.setSectionError(this.sectionMessage, this.$t('text.titleRequiredError'))
			}

			if (isValid === true && this.sectionMessage.message === this.$t('text.titleRequiredError')) {
				this.resetSectionError(this.sectionMessage)
			}
			this.$emit('update-product-title', this.product.fields.title)
			return isValid
		},
		validateLongDescription() {
			let isValid = true
			const hasAnyLocaleValue = this.validateTextLocales(this.product.fields.longDescription)
			this.longDescriptionErrors = []

			if (!hasAnyLocaleValue) {
				isValid = false
				this.longDescriptionErrors.push(this.$t('text.longDescriptionRequired'))
			}

			return isValid
		},
		validateShortDescription() {
			let isValid = true
			const hasAnyLocaleValue = this.validateTextLocales(this.product.fields.shortDescription)
			this.shortDescriptionErrors = []

			if (!this.productIsExternal && !hasAnyLocaleValue) {
				isValid = false
				this.shortDescriptionErrors.push(this.$t('text.shortDescriptionRequired'))
			}

			return isValid
		},
		onChange(field?: string): void {
			this.$emit('changed', this.hasChanged())
			if (!field) return

			if (field === 'title') {
				const titleChanged = JSON.stringify(this.product?.fields.title) !== JSON.stringify(this.initData.title)
				this.$emit('changeTitle', titleChanged)
			}
		},
		setInitData(): void {
			const initModel = JSON.parse(JSON.stringify(this.product))
			this.initData.title = initModel.fields.title
			this.initData.longDescription = initModel.fields.longDescription
			this.initData.shortDescription = initModel.fields.shortDescription
			this.initData.importantInformation = initModel.fields.importantInformation
		},
	},
	created() {
		this.setInitData()
	},
}
</script>
